import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { history } from '@umijs/max';

type RequestConfig<D = unknown> = AxiosRequestConfig<D>;
type RequestInterceptor = Parameters<AxiosInstance['interceptors']['request']['use']>[0];
type ResponseInterceptor<T = unknown> = (value: AxiosResponse<T>) => AxiosResponse<T> | Promise<AxiosResponse<T>>;
type ApiResponseLike = {
  code?: number;
};

const DEFAULT_TIMEOUT = 300000;
const JSON_CONTENT_TYPE = 'application/json;charset=UTF-8';
const FORM_CONTENT_TYPE = 'multipart/form-data';
const API_HOST_MAP = {
  dev: 'https://api-dev.yyzhhpt.com',
  online: 'https://api.yyzhhpt.com',
  test: 'https://api.yyzhhpt.com',
  pre: 'https://api.yyzhhpt.com',
};

const APP_ENV = UMI_APP_ENV || 'dev';
const API_HOST = API_HOST_MAP[APP_ENV as keyof typeof API_HOST_MAP] ?? API_HOST_MAP.dev;
const BASE_URL = process.env.NODE_ENV === 'development' ? '/manager' : `${API_HOST}/manager`;

const http = axios.create({
  baseURL: BASE_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': JSON_CONTENT_TYPE,
  },
});

import { getStoredDistrictCode, clearSelectedDistrictCode } from '@/utils/tools';

const defaultRequestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const districtCode = getStoredDistrictCode();
  if (districtCode != null) {
    config.headers['X-DistrictCode'] = String(districtCode);
  }
  return config;
};

const defaultResponseInterceptor = <T>(response: AxiosResponse<T>): T | Promise<never> => {
  const responseData = response.data as T & ApiResponseLike;
  if (responseData.code === 401 || responseData.code === 403) {
      localStorage.removeItem('token');
      localStorage.removeItem('roleType');
      clearSelectedDistrictCode();
      const currentPath = `${window.location.pathname}${window.location.search}`;
      const redirect = encodeURIComponent(currentPath);
      if (window.location.pathname !== '/user/login') {
        if(responseData.code === 403) {
          history.replace(`/user/login`);
        }else{
          history.replace(`/user/login?redirect=${redirect}`);
        }
      }
      let errorMessage = '登录已失效，请重新登录';
      if (responseData.code === 403) {
        errorMessage = '无权限访问，请联系管理员';
      }
      return Promise.reject(new Error(errorMessage));
  }   
  return response.data;
};

const defaultResponseErrorHandler = (error: AxiosError): Promise<never> => {
  if (error.response) {
    const { status, statusText } = error.response;
    const requestUrl = error.config?.url ?? '';

    if (status === 401 && !requestUrl.includes('/passport/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('roleType');
      clearSelectedDistrictCode();
      const currentPath = `${window.location.pathname}${window.location.search}`;
      const redirect = encodeURIComponent(currentPath);
      if (window.location.pathname !== '/user/login') {
        history.replace(`/user/login?redirect=${redirect}`);
      }
      return Promise.reject(new Error('登录已失效，请重新登录'));
    }

    return Promise.reject(new Error(`Request failed: ${status} ${statusText}`.trim()));
  }
  if (error.request) {
    return Promise.reject(new Error('No response received from server.'));
  }
  return Promise.reject(error);
};

http.interceptors.request.use(defaultRequestInterceptor, (error: AxiosError) => Promise.reject(error));
http.interceptors.response.use(defaultResponseInterceptor, defaultResponseErrorHandler);

export const addRequestInterceptor = (
  onFulfilled?: RequestInterceptor,
  onRejected?: (error: unknown) => unknown,
): number => http.interceptors.request.use(onFulfilled, onRejected);

export const addResponseInterceptor = <T = unknown>(
  onFulfilled?: ResponseInterceptor<T>,
  onRejected?: (error: unknown) => unknown,
): number => http.interceptors.response.use(onFulfilled, onRejected);

export function request<T = unknown, D = unknown>(config: RequestConfig<D>): Promise<T> {
  return http.request<T, T>(config);
}

export function get<T = unknown, P extends Record<string, unknown> = Record<string, unknown>>(
  url: string,
  params?: P,
  config?: Omit<RequestConfig, 'url' | 'method' | 'params'>,
): Promise<T> {
  return request<T>({
    ...config,
    url,
    method: 'GET',
    params,
  });
}

export function post<T = unknown, D = Record<string, unknown>>(
  url: string,
  data?: D,
  config?: Omit<RequestConfig<D>, 'url' | 'method' | 'data'>,
): Promise<T> {
  return request<T, D>({
    ...config,
    url,
    method: 'POST',
    data,
    headers: {
      'Content-Type': JSON_CONTENT_TYPE,
      ...(config?.headers || {}),
    },
  });
}

function buildFormData(data: Record<string, unknown>): FormData {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach((item) => formData.append(key, item as string | Blob));
      return;
    }
    if (value instanceof Blob) {
      formData.append(key, value);
      return;
    }
    formData.append(key, String(value));
  });
  return formData;
}

export function uploadFile<T = unknown>(
  url: string,
  data: FormData | Record<string, unknown>,
  config?: Omit<RequestConfig<FormData>, 'url' | 'method' | 'data'>,
): Promise<T> {
  const formData = data instanceof FormData ? data : buildFormData(data);
  return request<T, FormData>({
    ...config,
    url,
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': FORM_CONTENT_TYPE,
      ...(config?.headers || {}),
    },
  });
}

export default request;
